import type { AlterlyEffectInstance } from "../effects/effectRegistry";
import type { MediaAsset } from "../media/mediaTypes";
import { renderEffectStack } from "../../engine/cpu/renderEffectStack";
import { AppError } from "../../shared/errors/AppError";
import { assertExportMemoryBudget } from "./exportSafety";
import type { ExportResult, RasterExportOptions } from "./exportTypes";

function loadImage(url: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.decoding = "async";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new AppError("EXPORT_FAILED", "Could not decode the source image for export."));
    image.src = url;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, format: string, quality?: number) {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob);
      else reject(new AppError("EXPORT_FAILED", "The browser could not encode the exported image."));
    }, format, quality);
  });
}

function baseName(name: string) {
  return name.replace(/\.[^/.]+$/, "") || "alterly-export";
}

export async function exportRasterImage(
  asset: MediaAsset,
  effects: readonly AlterlyEffectInstance[],
  options: RasterExportOptions = { format: "image/png", scale: 1 },
): Promise<ExportResult> {
  if (asset.kind !== "image") {
    throw new AppError("EXPORT_FAILED", "Raster still export currently requires an image source.");
  }

  const scale = Math.max(0.25, Math.min(4, options.scale ?? 1));
  const width = Math.max(1, Math.round(asset.dimensions.width * scale));
  const height = Math.max(1, Math.round(asset.dimensions.height * scale));

  // CPU export can hold the source image, destination canvas, and temporary effect surfaces at once.
  assertExportMemoryBudget(width, height, Math.max(3, effects.length + 2));

  const image = await loadImage(asset.objectUrl);
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) throw new AppError("EXPORT_FAILED", "Canvas 2D is unavailable for export.");

  try {
    renderEffectStack(context, image, width, height, effects);
    const blob = await canvasToBlob(canvas, options.format, options.quality);
    const extension = options.format === "image/jpeg" ? "jpg" : "png";

    return {
      blob,
      filename: `${baseName(asset.name)}-alterly.${extension}`,
      mimeType: options.format,
    };
  } finally {
    canvas.width = 1;
    canvas.height = 1;
    image.src = "";
  }
}
